import React, { useState, useEffect, useRef, useCallback } from "react";

const areMenuIdArraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const MenuStateManager = ({ children, routeExpandedMenus = [] }) => {
  const [expandedMenus, setExpandedMenus] = useState([]);
  const autoExpandedMenusRef = useRef(new Set());
  const routeExpandedMenusRef = useRef([]);
  const navigationSearchSessionRef = useRef({
    active: false,
    snapshot: [],
    committedMenuIds: [],
  });

  useEffect(() => {
    const previousRouteExpandedMenus = new Set(routeExpandedMenusRef.current);
    const nextRouteExpandedMenus = Array.from(
      new Set((Array.isArray(routeExpandedMenus) ? routeExpandedMenus : []).filter(Boolean))
    );

    routeExpandedMenusRef.current = nextRouteExpandedMenus;

    setExpandedMenus((prevExpanded) => {
      const nextExpanded = prevExpanded.filter(
        (menuId) =>
          !previousRouteExpandedMenus.has(menuId) ||
          nextRouteExpandedMenus.includes(menuId)
      );

      nextRouteExpandedMenus.forEach((menuId) => {
        if (!nextExpanded.includes(menuId)) {
          nextExpanded.push(menuId);
        }
      });

      return areMenuIdArraysEqual(prevExpanded, nextExpanded)
        ? prevExpanded
        : nextExpanded;
    });
  }, [routeExpandedMenus]);

  const handleCollapseOthers = useCallback((activeParentIds = []) => {
    const parentIds = Array.from(
      new Set((Array.isArray(activeParentIds) ? activeParentIds : []).filter(Boolean))
    );

    setExpandedMenus((prevExpanded) => {
      const nextExpanded = prevExpanded.filter((menuId) =>
        parentIds.some(
          (parentId) =>
            menuId === parentId || menuId.startsWith(`${parentId}.`)
        )
      );

      return areMenuIdArraysEqual(prevExpanded, nextExpanded)
        ? prevExpanded
        : nextExpanded;
    });
  }, []);

  const syncAutoExpandedMenus = useCallback((menuIds = []) => {
    const previousAutoExpandedMenus = new Set(autoExpandedMenusRef.current);
    const nextAutoExpandedMenus = new Set(
      (Array.isArray(menuIds) ? menuIds : []).filter(Boolean)
    );

    autoExpandedMenusRef.current = nextAutoExpandedMenus;

    setExpandedMenus((prevExpanded) => {
      const nextExpanded = prevExpanded.filter(
        (menuId) =>
          !previousAutoExpandedMenus.has(menuId) ||
          nextAutoExpandedMenus.has(menuId)
      );

      nextAutoExpandedMenus.forEach((menuId) => {
        if (!nextExpanded.includes(menuId)) {
          nextExpanded.push(menuId);
        }
      });

      return areMenuIdArraysEqual(prevExpanded, nextExpanded)
        ? prevExpanded
        : nextExpanded;
    });
  }, []);

  const startNavigationSearchSession = useCallback(() => {
    if (navigationSearchSessionRef.current.active) {
      return;
    }

    navigationSearchSessionRef.current = {
      active: true,
      snapshot: expandedMenus.filter(
        (menuId) => !autoExpandedMenusRef.current.has(menuId)
      ),
      committedMenuIds: [],
    };
  }, [expandedMenus]);

  const commitNavigationSearchResult = useCallback(({ menuIds = [] } = {}) => {
    if (!navigationSearchSessionRef.current.active) {
      return;
    }

    navigationSearchSessionRef.current.committedMenuIds = Array.from(
      new Set((Array.isArray(menuIds) ? menuIds : []).filter(Boolean))
    );
  }, []);

  const endNavigationSearchSession = useCallback(() => {
    const currentSearchSession = navigationSearchSessionRef.current;

    autoExpandedMenusRef.current = new Set();

    if (!currentSearchSession.active) {
      return;
    }

    const restoredExpandedMenus = Array.from(
      new Set([
        ...currentSearchSession.snapshot,
        ...currentSearchSession.committedMenuIds,
      ])
    );

    navigationSearchSessionRef.current = {
      active: false,
      snapshot: [],
      committedMenuIds: [],
    };

    setExpandedMenus((prevExpanded) =>
      areMenuIdArraysEqual(prevExpanded, restoredExpandedMenus)
        ? prevExpanded
        : restoredExpandedMenus
    );
  }, []);

  const toggleMenu = useCallback((menuId) => {
    if (!menuId) {
      return;
    }

    setExpandedMenus((prevExpanded) => {
      if (prevExpanded.includes(menuId)) {
        return prevExpanded.filter(
          (existingMenuId) =>
            existingMenuId !== menuId &&
            !existingMenuId.startsWith(`${menuId}.`)
        );
      }

      return [...prevExpanded, menuId];
    });
  }, []);

  const stateData = {
    expandedMenus,
    handleCollapseOthers,
    startNavigationSearchSession,
    commitNavigationSearchResult,
    endNavigationSearchSession,
    syncAutoExpandedMenus,
    toggleMenu,
  };

  return children(stateData);
};

export default React.memo(MenuStateManager);
